import type { BackendErrorBody, BackendErrorType } from './types';
import { notifySessionGuard } from './sessionGuard';
import { useUiStore } from '@/store/uiStore';

/**
 * Низкоуровневый клиент бэкенда мини-приложения.
 *
 * Единая точка сетевых вызовов: cookie-сессия, разбор ошибок в машиночитаемый
 * `type`, таймаут. Весь остальной код работает с типизированными методами
 * `miniappApi`, а не с fetch напрямую.
 */

/**
 * Режимы:
 *   пусто          — адрес не задан: данных не будет, экраны покажут ошибку;
 *   `same-origin`  — относительные пути на тот же домен (рабочий режим);
 *   адрес          — другой домен (только отладка; на iOS ломается из-за cookie).
 */
const AUTH_MODE = ((import.meta.env.VITE_AUTH_BASE_URL as string | undefined) ?? '').trim();

const BASE_URL = AUTH_MODE === 'same-origin' ? '' : AUTH_MODE.replace(/\/$/, '');

export const isBackendConfigured = AUTH_MODE.length > 0;

const REQUEST_TIMEOUT_MS = 20000;

export class MiniappError extends Error {
  /** HTTP-статус; отсутствует при сетевом сбое. */
  status?: number;
  /** Машиночитаемый тип из тела ответа: по нему фронт выбирает реакцию. */
  type?: BackendErrorType;

  constructor(message: string, options: { status?: number; type?: BackendErrorType } = {}) {
    super(message);
    this.name = 'MiniappError';
    this.status = options.status;
    this.type = options.type;
  }

  /** Сессия недействительна: нужен повторный вход, а не повтор запроса. */
  get isSessionLost(): boolean {
    return this.type === 'MINIAPP_SESSION_NOT_FOUND' || this.type === 'MINIAPP_SESSION_REPLACED';
  }

  get isRegistrationRequired(): boolean {
    return this.status === 403 && this.type === 'REGISTRATION_REQUIRED';
  }
}

type Query = Record<string, string | number | undefined>;

function buildUrl(path: string, query?: Query): string {
  const url = `${BASE_URL}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Абсолютный адрес для ссылок, которые бэкенд отдаёт готовыми (вложения чата).
 * В рабочем режиме `same-origin` возвращается как есть; префикс нужен только
 * при отладке с другого домена.
 */
export function resolveApiUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${BASE_URL}${path}`;
}

/**
 * Текст ошибки от сервера, если он есть.
 *
 * Бэкенд присылает формулировки на русском и по делу («Сессия истекла»,
 * «В аудио не распознана речь») — они точнее общего запасного текста. Сбой
 * сети до сервера не доходит, и тогда показывается запасной.
 */
export function messageOf(error: unknown, fallback: string): string {
  return error instanceof MiniappError ? error.message : fallback;
}

interface RequestInitEx extends RequestInit {
  query?: Query;
  /** Отдельный таймаут: загрузка файла с телефона длится дольше обычного запроса. */
  timeoutMs?: number;
}

async function request<T>(path: string, init: RequestInitEx = {}): Promise<T> {
  const { query, timeoutMs, headers, ...rest } = init;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs ?? REQUEST_TIMEOUT_MS);

  // Для multipart заголовок ставит браузер: только он знает boundary
  const isForm = typeof FormData !== 'undefined' && init.body instanceof FormData;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      // Без include браузер не отправит и не примет cookie сессии
      credentials: 'include',
      ...rest,
      headers: isForm ? { ...headers } : { 'Content-Type': 'application/json', ...headers },
      signal: controller.signal,
    });
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    throw new MiniappError(aborted ? 'Превышено время ожидания ответа' : 'Нет подключения к интернету');
  } finally {
    clearTimeout(timer);
  }

  // Ответ сервера — доказательство связи. В части клиентов `navigator.onLine`
  // ложно сообщает об отсутствии сети, и плашка висела бы поверх рабочего
  // приложения
  useUiStore.getState().setOnline(true);

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as BackendErrorBody | null;
    const upstream = upstreamStatus(body);
    const detail = describeError(body);

    // Реакция на потерю сессии одинакова для всех ручек, поэтому она здесь,
    // а не в каждом сторе по отдельности
    notifySessionGuard(path, upstream ?? response.status, body?.type);
    throw new MiniappError(detail.message, {
      status: upstream ?? response.status,
      type: body?.type ?? detail.type,
    });
  }

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => undefined)) as T;
  assertNotSilentFailure(payload);
  return payload;
}

/**
 * Ручки AI-чата отвечают 200 даже когда операция не выполнена: исключение на
 * стороне бэкенда глушится, а в тело кладётся `{ message: null }` без `data`.
 * Считать такой ответ успехом нельзя — приложение сообщало бы об успехе там,
 * где ничего не произошло.
 *
 * Пустое `message` — единственный надёжный признак: при успехе сдачи бэкенд
 * возвращает `{ message: "Спасибо, мы приняли Ваш ответ" }`, тоже без `data`,
 * поэтому наличие текста трактуется как успех. Итог сдачи всё равно
 * подтверждается отметкой урока, а не текстом ответа.
 */
function assertNotSilentFailure(payload: unknown): void {
  if (!payload || typeof payload !== 'object') return;

  const body = payload as Record<string, unknown>;
  if (body.data !== undefined) return;
  if (!('message' in body)) return;

  // Текст в `message` — это успех: при принятой сдаче бэкенд отвечает
  // благодарностью и тоже без `data`
  if (typeof body.message === 'string' && body.message.trim().length > 0) return;

  // Полезные поля рядом с `message` означают, что ответ содержателен:
  // так выглядит продолжение активного чата
  const hasOtherData = Object.entries(body).some(
    ([key, value]) => key !== 'message' && value !== null && value !== undefined
  );
  if (hasOtherData) return;

  throw new MiniappError('Сервер не выполнил операцию', { status: 200, type: 'SILENT_FAILURE' });
}

/**
 * Текст и код ошибки из тела ответа.
 *
 * Обычно `message` — строка, но часть ручек передаёт наружу ответ вышестоящего
 * сервиса объектом: так отвечает загрузка аудио, где причина лежит в
 * `message.detail`. Без разбора пользователь увидел бы «[object Object]», а код
 * причины потерялся бы.
 */
function describeError(body: BackendErrorBody | null): { message: string; type?: string } {
  const message = body?.message;

  if (typeof message === 'string' && message.trim()) return { message };

  if (message && typeof message === 'object') {
    const detail = message.detail;
    if (detail?.message) return { message: detail.message, type: detail.code };
    if (detail?.code) return { message: 'Запрос не выполнен', type: detail.code };
  }

  return { message: 'Запрос не выполнен' };
}

/**
 * Код ошибки AI-сервиса, спрятанный внутри ответа бэкенда.
 *
 * Часть сбоев приходит как 500 с телом вида
 * `{ name: "AxiosError", message: "Request failed with status code 409" }`:
 * настоящая причина видна только в тексте. Без этого 409 «задача уже
 * выполняется» неотличим от общего сбоя сервера.
 */
function upstreamStatus(body: BackendErrorBody | null): number | undefined {
  if (body?.name !== 'AxiosError' || typeof body.message !== 'string') return undefined;
  const match = /status code (\d{3})/.exec(body.message);
  return match ? Number(match[1]) : undefined;
}

const UPLOAD_TIMEOUT_MS = 120000;

export const http = {
  get: <T,>(path: string, query?: Query) => request<T>(path, { method: 'GET', query }),
  post: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  /** Multipart: `Content-Type` намеренно не выставляется. */
  postForm: <T,>(path: string, form: FormData) =>
    request<T>(path, { method: 'POST', body: form, timeoutMs: UPLOAD_TIMEOUT_MS }),
};

/**
 * Скачивание файла через прокси бэкенда. Отдельно от `http`, потому что ответ —
 * поток файла, а не JSON: разбор тела здесь неприменим.
 */
export async function fetchApiBlob(path: string): Promise<Blob> {
  let response: Response;
  try {
    response = await fetch(resolveApiUrl(path), { credentials: 'include' });
  } catch {
    throw new MiniappError('Нет подключения к интернету');
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as BackendErrorBody | null;
    const detail = describeError(body);
    notifySessionGuard(path, response.status, body?.type);
    throw new MiniappError(body ? detail.message : 'Не удалось получить файл', {
      status: response.status,
      type: body?.type ?? detail.type,
    });
  }

  return response.blob();
}
